import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api } from 'lib/request/api';
import type { MineralType } from 'types/minerals';
import type { DcaForecastParams, DcaRampUp } from 'types/api-responses';
import type { StreamFitSegments } from './dca-fit-atoms';
import { useTcDcaForm } from './tc-dca-form-context';
import type {
  TcDcaFormValues,
  StreamDcaParams,
  StreamRegressionParams,
  TcDcaRegressionRequest,
  TcDcaRegressionResponse,
  StreamRegressionResult,
  AllLockStates,
  DeclineModel,
  RampUpModel,
} from './types';

// ---------------------------------------------------------------------------
// Parameter maps — which params each model exposes for regression
// ---------------------------------------------------------------------------

const DCA_PARAMETER_MAP: Record<string, string[]> = {
  modified_hyperbolic: ['qi', 'di', 'bFactor', 'dlim'],
  hyperbolic: ['qi', 'di', 'bFactor'],
  exponential: ['qi', 'di'],
};

const RAMP_UP_PARAMETER_MAP: Record<string, string[]> = {
  linear: ['qi', 'n'],
  exponential: ['qi', 'n'],
  constant: ['qi'],
};

/** Decline params a fit can report a value for, in `optimizedParameters`. */
const DCA_LOCKABLE_PARAMS = ['qi', 'di', 'bFactor', 'dlim'] as const;

// Convert camelCase to snake_case for API
function toSnakeCase(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

/**
 * Returns true when the regression response contains actual ramp-up params.
 * @param {unknown} rampUp - The ramp-up value from the regression response.
 * @return {boolean} Whether the ramp-up contains real data.
 */
function hasRampUpResult(rampUp: unknown): rampUp is Record<string, number> {
  return (
    rampUp !== null &&
    rampUp !== undefined &&
    typeof rampUp === 'object' &&
    Object.keys(rampUp as object).length > 0
  );
}

// ---------------------------------------------------------------------------
// Build unlocked parameters list (all params for the model minus locked ones)
// ---------------------------------------------------------------------------

function getUnlockedParams(
  dcaModel: DeclineModel,
  rampUpModel: RampUpModel,
  locks: { dca: Record<string, number>; rampUp: Record<string, number> }
): { dca: string[]; rampUp: string[] } {
  const dcaExpected = DCA_PARAMETER_MAP[dcaModel] ?? [];
  const lockedDcaKeys = Object.keys(locks.dca);
  const unlockedDca = dcaExpected
    .filter((p) => !lockedDcaKeys.includes(p))
    .map(toSnakeCase);

  const rampUpExpected =
    rampUpModel !== 'none' ? (RAMP_UP_PARAMETER_MAP[rampUpModel] ?? []) : [];
  const lockedRampUpKeys = Object.keys(locks.rampUp);
  const unlockedRampUp = rampUpExpected
    .filter((p) => !lockedRampUpKeys.includes(p))
    .map(toSnakeCase);

  return { dca: unlockedDca, rampUp: unlockedRampUp };
}

// ---------------------------------------------------------------------------
// Build per-stream regression params from form values + lock state
// ---------------------------------------------------------------------------

function buildStreamRegressionParams(
  streamParams: StreamDcaParams,
  formValues: TcDcaFormValues,
  locks: { dca: Record<string, number>; rampUp: Record<string, number> }
): StreamRegressionParams {
  const unlocked = getUnlockedParams(
    streamParams.dcaModel,
    streamParams.rampUpModel,
    locks
  );

  // Convert locked param keys to snake_case for the API
  const lockedDca: Record<string, number> = {};
  for (const [key, value] of Object.entries(locks.dca)) {
    lockedDca[toSnakeCase(key)] = value;
  }

  const result: StreamRegressionParams = {
    dcaModel: streamParams.dcaModel,
    qiFormat: formValues.qiFormat,
    durationPeriod: formValues.forecastDurationPeriod,
    durationFormat: 'years',
    lockedParameters: lockedDca,
    unlockedParameters: unlocked.dca,
  };

  if (streamParams.rampUpModel !== 'none') {
    const lockedRampUp: Record<string, number> = {};
    for (const [key, value] of Object.entries(locks.rampUp)) {
      lockedRampUp[toSnakeCase(key)] = value;
    }
    result.rampUp = {
      model: streamParams.rampUpModel as 'constant' | 'linear' | 'exponential',
      lockedParameters: lockedRampUp,
      unlockedParameters: unlocked.rampUp,
    };
  }

  return result;
}

// ---------------------------------------------------------------------------
// Build full request payload
// ---------------------------------------------------------------------------

const STREAM_DCA_PARAMS_KEY: Record<
  MineralType,
  'oilDcaParams' | 'gasDcaParams' | 'waterDcaParams'
> = {
  oil: 'oilDcaParams',
  gas: 'gasDcaParams',
  water: 'waterDcaParams',
};

function buildRegressionPayload(
  formValues: TcDcaFormValues,
  lockStates: AllLockStates,
  uwis: string[],
  aggregation: string,
  typeCurveHeaderDetailsId: number,
  dcaScenarioId: number,
  stream: MineralType
): TcDcaRegressionRequest {
  const dcaParams: TcDcaRegressionRequest['typeCurveDcaRegression']['dcaParams'] =
    {
      diFormat: formValues.diFormat,
      forecastDurationOption: formValues.forecastDurationOption,
    };

  const formKey = STREAM_DCA_PARAMS_KEY[stream];
  dcaParams[formKey] = buildStreamRegressionParams(
    formValues[formKey],
    formValues,
    lockStates[stream]
  );

  return {
    typeCurveDcaRegression: {
      uwis,
      aggregation,
      dcaScenarioId,
      typeCurveHeaderDetailsId,
      dcaParams,
    },
  };
}

// ---------------------------------------------------------------------------
// Apply regression results to form values
// ---------------------------------------------------------------------------

function applyResultToStream(
  result: StreamRegressionResult,
  currentParams: StreamDcaParams
): Partial<StreamDcaParams> {
  const { optimizedParameters } = result;

  const updates: Partial<StreamDcaParams> = {
    qi: optimizedParameters.qi ?? currentParams.qi,
    di: optimizedParameters.di ?? currentParams.di,
  };

  if (optimizedParameters.bFactor !== undefined) {
    updates.bFactor = optimizedParameters.bFactor;
  }
  if (optimizedParameters.dlim !== undefined) {
    updates.dlim = optimizedParameters.dlim;
  }

  // The response can return a different decline model than the one submitted.
  // Write it back so the form stays the complete record of the fit — Save reads
  // the form, not the response. `model` is typed as a bare string, so validate
  // it against the known models before trusting it.
  if (DCA_PARAMETER_MAP[optimizedParameters.model]) {
    updates.dcaModel = optimizedParameters.model as DeclineModel;
  }

  // Ramp-up optimized params
  if (
    hasRampUpResult(optimizedParameters.rampUp) &&
    currentParams.rampUpModel !== 'none'
  ) {
    const ramp = optimizedParameters.rampUp as Record<string, number>;
    if (ramp.qi !== undefined) updates.rampUpQi = ramp.qi;
    if (ramp.n !== undefined) updates.rampUpN = ramp.n;
    if (ramp.tRamp !== undefined) updates.rampUpDuration = ramp.tRamp;
  }

  return updates;
}

// ---------------------------------------------------------------------------
// Build the persisted snake_case `dca_params` from a fit result + form context
// ---------------------------------------------------------------------------

function buildFitDcaParams(
  result: StreamRegressionResult,
  formValues: TcDcaFormValues,
  currentParams: StreamDcaParams
): DcaForecastParams {
  const { optimizedParameters: opt } = result;

  let rampUp: DcaRampUp | null = null;
  if (currentParams.rampUpModel !== 'none' && hasRampUpResult(opt.rampUp)) {
    const ramp = opt.rampUp as Record<string, number>;
    rampUp = {
      model: currentParams.rampUpModel,
      qi: ramp.qi ?? currentParams.rampUpQi ?? 0,
      n: ramp.n ?? currentParams.rampUpN ?? 0,
      t_ramp: ramp.tRamp ?? currentParams.rampUpDuration ?? 1,
    };
  }

  return {
    di: opt.di,
    qi: opt.qi,
    dlim: opt.dlim,
    b_factor: opt.bFactor,
    dca_model: opt.model ?? currentParams.dcaModel,
    ramp_up: rampUp,
    di_format: formValues.diFormat,
    qi_format: formValues.qiFormat,
    // The TC DCA form exposes no time-format control; the DCA flow expresses
    // production time in months, matching the persisted default.
    time_format: 'months',
    duration_format: 'years',
    duration_period: formValues.forecastDurationPeriod,
  };
}

// ---------------------------------------------------------------------------
// Build persisted snake_case `dca_params` directly from the form (no fit needed)
// ---------------------------------------------------------------------------

export function buildFormDcaParams(
  formValues: TcDcaFormValues,
  stream: MineralType
): DcaForecastParams {
  const params = formValues[STREAM_DCA_PARAMS_KEY[stream]] as StreamDcaParams;

  let rampUp: DcaRampUp | null = null;
  if (params.rampUpModel !== 'none') {
    rampUp = {
      model: params.rampUpModel,
      qi: params.rampUpQi ?? 0,
      n: params.rampUpN ?? 0,
      t_ramp: params.rampUpDuration ?? 1,
    };
  }

  return {
    di: params.di ?? 0,
    qi: params.qi ?? 0,
    dlim: params.dlim ?? 0,
    b_factor: params.bFactor ?? 0,
    dca_model: params.dcaModel,
    ramp_up: rampUp,
    di_format: formValues.diFormat,
    qi_format: formValues.qiFormat,
    time_format: 'months',
    duration_format: 'years',
    duration_period: formValues.forecastDurationPeriod,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseTcDcaRegressionOptions {
  uwis: string[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  aggregation: string;
  stream: MineralType;
}

/** Local fit results accumulated per stream — committed to global atoms on Save. */
export interface FitResults {
  params: Partial<Record<MineralType, DcaForecastParams>>;
  segments: Partial<Record<MineralType, StreamFitSegments>>;
  aggregations: Partial<Record<MineralType, string>>;
  scenarioIds: Partial<Record<MineralType, number>>;
}

const EMPTY_FIT_RESULTS: FitResults = {
  params: {},
  segments: {},
  aggregations: {},
  scenarioIds: {},
};

export function useTcDcaRegression({
  uwis,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  aggregation,
  stream,
}: UseTcDcaRegressionOptions) {
  const { form, lockStates, setLockParam } = useTcDcaForm();

  const [fitResults, setFitResults] = useState<FitResults>(EMPTY_FIT_RESULTS);

  const mutation = useMutation<
    TcDcaRegressionResponse,
    Error,
    TcDcaRegressionRequest
  >({
    mutationFn: (payload) =>
      api
        .post('reservoir/type_curve_dca_regressions', { json: payload })
        .json<TcDcaRegressionResponse>(),
  });

  const fit = useCallback(() => {
    // The regression requires a numeric type-curve header-detail id; bail if the
    // active type curve hasn't resolved to one yet.
    if (typeCurveHeaderDetailsId === null) return;

    const formValues = form.state.values;

    const payload = buildRegressionPayload(
      formValues,
      lockStates,
      uwis,
      aggregation,
      typeCurveHeaderDetailsId,
      dcaScenarioId,
      stream
    );

    mutation.mutate(payload, {
      onSuccess: (data) => {
        const { results } = data.typeCurveDcaRegressionResult;

        const STREAMS: MineralType[] = ['oil', 'gas', 'water'];
        const FIELD_MAP: Record<string, keyof TcDcaFormValues> = {
          oil: 'oilDcaParams',
          gas: 'gasDcaParams',
          water: 'waterDcaParams',
        };

        for (const stream of STREAMS) {
          const streamResult = results[stream];
          if (!streamResult) continue;

          const fieldKey = FIELD_MAP[stream];
          const currentParams = formValues[fieldKey] as StreamDcaParams;
          const updates = applyResultToStream(streamResult, currentParams);

          // Merge updates into form
          form.setFieldValue(fieldKey, {
            ...currentParams,
            ...updates,
          } as never);

          // Stash the fit hyper-params (snake_case) into local state so the
          // modal's Save handler can commit them atomically.
          const dcaParams = buildFitDcaParams(
            streamResult,
            formValues,
            currentParams
          );
          setFitResults((prev) => ({
            ...prev,
            params: { ...prev.params, [stream]: dcaParams },
            segments: {
              ...prev.segments,
              [stream]: {
                segmentMonthsOnline: (streamResult.segmentStartDates ?? [])
                  .map(Number)
                  .filter(Number.isFinite),
                outlierMonthsOnline: (streamResult.outlierDates ?? [])
                  .map(Number)
                  .filter(Number.isFinite),
              },
            },
          }));

          // Refresh the fitted value on params that are already locked, so they
          // persist into the next fit. A fit must not introduce new locks: the
          // locked set comes from the DCA scenario's `lockedParameters` (via
          // extractInitialLockStates) plus any the user locked by hand, and a
          // param neither of those holds stays editable after fitting.
          const { optimizedParameters } = streamResult;
          const currentlyLocked = lockStates[stream].dca;
          for (const param of DCA_LOCKABLE_PARAMS) {
            const fitted = optimizedParameters[param];
            if (fitted !== undefined && param in currentlyLocked) {
              setLockParam(stream, 'dca', param, fitted);
            }
          }
        }

        // Store the aggregation and scenario ID used for this fit per stream
        // into local state (committed to global atoms on Save).
        setFitResults((prev) => {
          const newAggregations = { ...prev.aggregations };
          const newScenarioIds = { ...prev.scenarioIds };
          for (const s of STREAMS) {
            if (results[s]) {
              newAggregations[s] = aggregation;
              newScenarioIds[s] = dcaScenarioId;
            }
          }
          return {
            ...prev,
            aggregations: newAggregations,
            scenarioIds: newScenarioIds,
          };
        });
      },
    });
  }, [
    form,
    lockStates,
    uwis,
    aggregation,
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    stream,
    mutation,
    setLockParam,
  ]);

  return {
    fit,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data ?? null,
    fitResults,
  };
}
